import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  Box,
  Tabs,
  Tab,
  Divider,
  Fade,
  Zoom,
  LinearProgress,
  Avatar,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as PowerIcon,
  History as HistoryIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  NotificationsActive as NotificationsActiveIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { backend_url } from "../App";
import { setSnackBar } from "../reusable_components/site_data/siteDataSlice";

import {
  fetchAlarmDevices,
  createAlarmDevice,
  updateAlarmDevice,
  deleteAlarmDevice,
  testAlarmConnection,
  sendAlarmCommand,
  createAlarmAssignment,
  updateAlarmAssignment,
  deleteAlarmAssignment,
  fetchAlarmLogs,
  fetchActiveAlarms,
  fetchCamerasWithAlarm,
  selectAlarmDevices,
  selectAlarmAssignments,
  selectAlarmLogs,
  selectActiveAlarms,
} from "../components/Alarm/alarmSlice";
import Alarm from "../components/Alarm/Alarm";

const AlarmManagement = () => {
  const [tab, setTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [testingDevice, setTestingDevice] = useState(null);
  const [openEditAssignmentDialog, setOpenEditAssignmentDialog] =
    useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);

  // Camera list for assignment dropdown
  const [availableCameras, setAvailableCameras] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);

  // Pagination states
  const [assignmentsPage, setAssignmentsPage] = useState(0);
  const [assignmentsRowsPerPage, setAssignmentsRowsPerPage] = useState(10);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10);

  // User list for created_by dropdown (for superuser)
  const [userList, setUserList] = useState([]);

  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperUser = user?.username === "lumsadmin" || user?.is_staff === true;
  const isPunjabAdmin = user?.username === "punjabadmin";

  // Known user IDs
  const LUMSADMIN_ID = 1;
  const PUNJABADMIN_ID = 19;

  const [formData, setFormData] = useState({
    device_id: "",
    name: "",
    description: "",
    mqtt_host: "2be1374228c54154bc14422981467fff.s2.eu.hivemq.cloud",
    mqtt_port: 8883,
    mqtt_username: "hivemqtt",
    mqtt_password: "Hivemqtt@1122",
    mqtt_use_ssl: true,
    status: "ACTIVE",
    command_topic: "fire/{device_id}/cmd",
    state_topic: "fire/{device_id}/state",
    created_by: LUMSADMIN_ID, // Default to lumsadmin (ID 1)
  });

  const [assignmentForm, setAssignmentForm] = useState({
    camera: "",
    alarm_device: "",
    auto_trigger: true,
    trigger_duration: 180,
    is_active: true,
  });

  const dispatch = useDispatch();
  const devices = useSelector(selectAlarmDevices);
  const assignments = useSelector(selectAlarmAssignments);
  const logs = useSelector(selectAlarmLogs);
  const activeAlarms = useSelector(selectActiveAlarms);

  // 3D effect styles matching WeatherStations
  const card3D = {
    background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
    borderRadius: "20px",
    boxShadow: "20px 20px 60px #d9d9d9, -20px -20px 60px #ffffff",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "25px 25px 75px #d9d9d9, -25px -25px 75px #ffffff",
    },
  };

  // Create a Set of available camera IDs for quick lookup
  const availableCameraIds = useMemo(() => {
    return new Set(availableCameras.map((cam) => cam.id));
  }, [availableCameras]);

  // Filter alarm devices for punjabadmin
  useEffect(() => {
    if (devices?.results) {
      if (isPunjabAdmin) {
        // Show only devices created by punjabadmin (created_by === PUNJABADMIN_ID)
        const filtered = devices.results.filter(
          (device) => device.created_by === PUNJABADMIN_ID,
        );
        setFilteredDevices(filtered);
        console.log(
          `Filtered devices for punjabadmin: ${filtered.length} out of ${devices.results.length}`,
        );
      } else {
        setFilteredDevices(devices.results || []);
      }
    }
  }, [devices, isPunjabAdmin]);

  // Fetch all pages of assignments
  const fetchAllAssignments = useCallback(async () => {
    try {
      console.log("Fetching all assignments...");
      let allResults = [];
      let nextUrl = `${backend_url}/core/api/alarm-assignments/?page_size=100`;

      while (nextUrl) {
        const response = await fetch(nextUrl, {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
        const data = await response.json();
        allResults = [...allResults, ...(data.results || [])];
        nextUrl = data.next;
      }

      // Sort assignments by assigned_at (newest to oldest)
      allResults.sort(
        (a, b) => new Date(b.assigned_at) - new Date(a.assigned_at),
      );

      console.log(`Fetched ${allResults.length} assignments total`);
      setAllAssignments(allResults);
      return allResults;
    } catch (error) {
      console.error("Error fetching all assignments:", error);
      return [];
    }
  }, []);

  // Filter assignments for punjabadmin
  useEffect(() => {
    if (allAssignments.length > 0) {
      if (isPunjabAdmin) {
        // Show assignments that are:
        // 1. Created by punjabadmin (assigned_by === PUNJABADMIN_ID), OR
        // 2. Associated with cameras that punjabadmin has access to
        const filtered = allAssignments.filter((assignment) => {
          const isCreatedByPunjabAdmin =
            assignment.assigned_by === PUNJABADMIN_ID;
          const isCameraAccessible = availableCameraIds.has(assignment.camera);

          return isCreatedByPunjabAdmin || isCameraAccessible;
        });

        setFilteredAssignments(filtered);
        console.log(
          `Filtered assignments for punjabadmin: ${filtered.length} out of ${allAssignments.length}`,
        );
      } else {
        setFilteredAssignments(allAssignments);
      }
    } else if (assignments?.results) {
      // Fallback to results if allAssignments not available
      const sortedResults = [...(assignments.results || [])].sort(
        (a, b) => new Date(b.assigned_at) - new Date(a.assigned_at),
      );

      if (isPunjabAdmin) {
        const filtered = sortedResults.filter((assignment) => {
          const isCreatedByPunjabAdmin =
            assignment.assigned_by === PUNJABADMIN_ID;
          const isCameraAccessible = availableCameraIds.has(assignment.camera);

          return isCreatedByPunjabAdmin || isCameraAccessible;
        });
        setFilteredAssignments(filtered);
      } else {
        setFilteredAssignments(sortedResults);
      }
    }
  }, [assignments, allAssignments, availableCameraIds, isPunjabAdmin]);

  // Filter logs for punjabadmin
  useEffect(() => {
    if (logs?.results) {
      if (isPunjabAdmin) {
        // Filter logs to only show those triggered by punjabadmin
        const filtered = logs.results.filter(
          (log) => log.triggered_by_name === "punjabadmin",
        );

        setFilteredLogs(filtered);
        console.log(
          `Filtered logs for punjabadmin by triggered_by_name: ${filtered.length} out of ${logs.results.length}`,
        );
      } else {
        setFilteredLogs(logs.results || []);
      }
    }
  }, [logs, isPunjabAdmin]);

  // Initialize user list with known users
  useEffect(() => {
    // Set up the user list with known users
    const knownUsers = [
      { id: LUMSADMIN_ID, username: "lumsadmin", name: "lumsadmin" },
      { id: PUNJABADMIN_ID, username: "punjabadmin", name: "punjabadmin" },
    ];
    setUserList(knownUsers);
    console.log("User list initialized:", knownUsers);
  }, []);

  // Define loadData with useCallback
  const loadData = useCallback(async () => {
    dispatch(fetchAlarmDevices());

    // Fetch all assignments (all pages)
    await fetchAllAssignments();

    dispatch(fetchAlarmLogs({ page_size: 100 }));
    dispatch(fetchActiveAlarms());
    dispatch(fetchCamerasWithAlarm());
  }, [dispatch, fetchAllAssignments]);

  // Define fetchAvailableCameras with useCallback
  const fetchAvailableCameras = useCallback(async () => {
    try {
      const response = await fetch(`${backend_url}/core/api/camera/`, {
        headers: {
          Authorization: `Token ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      const cameras = data.results || [];
      setAvailableCameras(cameras);

      console.log(`Available cameras for ${user?.username}: ${cameras.length}`);

      return cameras;
    } catch (error) {
      console.error("Failed to fetch cameras:", error);
      dispatch(setSnackBar("Failed to fetch cameras"));
      return [];
    }
  }, [dispatch, user?.username]);

  useEffect(() => {
    loadData();
    fetchAvailableCameras();
  }, [loadData, fetchAvailableCameras]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleOpenDialog = (device = null) => {
    // Only superuser can open add/edit dialog
    if (!isSuperUser) return;

    if (device) {
      console.log("Editing device:", device);
      setEditingDevice(device);
      setFormData({
        device_id: device.device_id || "",
        name: device.name || "",
        description: device.description || "",
        mqtt_host:
          device.mqtt_host ||
          "2be1374228c54154bc14422981467fff.s2.eu.hivemq.cloud",
        mqtt_port: device.mqtt_port || 8883,
        mqtt_username: device.mqtt_username || "hivemqtt",
        mqtt_password: device.mqtt_password || "Hivemqtt@1122",
        mqtt_use_ssl:
          device.mqtt_use_ssl !== undefined ? device.mqtt_use_ssl : true,
        status: device.status || "ACTIVE",
        command_topic: device.command_topic || "fire/{device_id}/cmd",
        state_topic: device.state_topic || "fire/{device_id}/state",
        created_by: device.created_by || LUMSADMIN_ID,
      });
    } else {
      setEditingDevice(null);
      setFormData({
        device_id: "",
        name: "",
        description: "",
        mqtt_host: "2be1374228c54154bc14422981467fff.s2.eu.hivemq.cloud",
        mqtt_port: 8883,
        mqtt_username: "hivemqtt",
        mqtt_password: "Hivemqtt@1122",
        mqtt_use_ssl: true,
        status: "ACTIVE",
        command_topic: "fire/{device_id}/cmd",
        state_topic: "fire/{device_id}/state",
        created_by: LUMSADMIN_ID, // Default to lumsadmin
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDevice(null);
  };

  const handleSaveDevice = async () => {
    // Only superuser can save devices
    if (!isSuperUser) return;

    try {
      console.log("Saving device with data:", formData);
      console.log("Created by value being sent:", formData.created_by);

      if (editingDevice) {
        // Update existing device
        console.log("Updating device ID:", editingDevice.id);
        const result = await dispatch(
          updateAlarmDevice({
            id: editingDevice.id,
            data: {
              device_id: formData.device_id,
              name: formData.name,
              description: formData.description,
              mqtt_host: formData.mqtt_host,
              mqtt_port: formData.mqtt_port,
              mqtt_username: formData.mqtt_username,
              mqtt_password: formData.mqtt_password,
              mqtt_use_ssl: formData.mqtt_use_ssl,
              status: formData.status,
              command_topic: formData.command_topic,
              state_topic: formData.state_topic,
              created_by: formData.created_by, // Include created_by in update
            },
          }),
        ).unwrap();
        console.log("Update result:", result);
      } else {
        // Create new device
        console.log("Creating new device with data:", formData);
        const result = await dispatch(createAlarmDevice(formData)).unwrap();
        console.log("Create result:", result);
      }

      handleCloseDialog();
      loadData(); // Refresh data after save
    } catch (error) {
      console.error("Error saving device:", error);
      dispatch(setSnackBar(error?.message || "Failed to save device"));
    }
  };

  const handleDeleteDevice = (id) => {
    // Only superuser can delete devices
    if (!isSuperUser) return;

    if (window.confirm("Are you sure you want to delete this alarm device?")) {
      dispatch(deleteAlarmDevice(id));
    }
  };

  const handleTestConnection = (id) => {
    dispatch(testAlarmConnection(id));
  };

  const handleSendCommand = (device, command) => {
    dispatch(
      sendAlarmCommand({
        id: device.id,
        deviceId: device.device_id,
        command,
        triggerType: "MANUAL_TEST",
        eventUuid: null,
      }),
    );
  };

  const handleOpenAssign = (device) => {
    setSelectedDevice(device);
    setAssignmentForm({
      camera: "",
      alarm_device: device.id,
      auto_trigger: true,
      trigger_duration: 180,
      is_active: true,
    });
    setOpenAssignDialog(true);
  };

  const handleCloseAssign = () => {
    setOpenAssignDialog(false);
    setSelectedDevice(null);
    setAssignmentForm({
      camera: "",
      alarm_device: "",
      auto_trigger: true,
      trigger_duration: 180,
      is_active: true,
    });
  };

  const handleSaveAssignment = async () => {
    try {
      console.log("Saving assignment with data:", assignmentForm);

      if (!assignmentForm.camera) {
        dispatch(setSnackBar("Please select a camera"));
        return;
      }

      const result = await dispatch(
        createAlarmAssignment(assignmentForm),
      ).unwrap();

      console.log("Assignment successful:", result);
      dispatch(setSnackBar("Camera assigned successfully!"));

      handleCloseAssign();
      loadData();
    } catch (error) {
      console.error("Failed to create assignment:", error);
    }
  };

  const handleOpenEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setAssignmentForm({
      camera: assignment.camera,
      alarm_device: assignment.alarm_device,
      auto_trigger: assignment.auto_trigger,
      trigger_duration: assignment.trigger_duration,
      is_active: assignment.is_active,
    });
    setOpenEditAssignmentDialog(true);
  };

  const handleCloseEditAssignment = () => {
    setOpenEditAssignmentDialog(false);
    setEditingAssignment(null);
    setAssignmentForm({
      camera: "",
      alarm_device: "",
      auto_trigger: true,
      trigger_duration: 180,
      is_active: true,
    });
  };

  const handleUpdateAssignment = async () => {
    try {
      if (!editingAssignment) return;

      const result = await dispatch(
        updateAlarmAssignment({
          id: editingAssignment.id,
          data: assignmentForm,
        }),
      ).unwrap();

      console.log("Assignment updated:", result);
      dispatch(setSnackBar("Assignment updated successfully!"));

      handleCloseEditAssignment();
      loadData();
    } catch (error) {
      console.error("Failed to update assignment:", error);
    }
  };

  const handleDeleteAssignment = (id) => {
    // Only superuser can delete assignments
    if (!isSuperUser) return;

    if (window.confirm("Are you sure you want to delete this assignment?")) {
      dispatch(deleteAlarmAssignment(id)).then(() => {
        loadData();
      });
    }
  };

  const handleCloseTest = () => {
    setOpenTestDialog(false);
    setTestingDevice(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "default";
      case "MAINTENANCE":
        return "warning";
      default:
        return "default";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Pagination handlers
  const handleAssignmentsChangePage = (event, newPage) => {
    setAssignmentsPage(newPage);
  };

  const handleAssignmentsChangeRowsPerPage = (event) => {
    setAssignmentsRowsPerPage(parseInt(event.target.value, 10));
    setAssignmentsPage(0);
  };

  const handleLogsChangePage = (event, newPage) => {
    setLogsPage(newPage);
  };

  const handleLogsChangeRowsPerPage = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10));
    setLogsPage(0);
  };

  // Get display data based on user role
  const displayDevices = isPunjabAdmin
    ? filteredDevices
    : devices.results || [];
  const displayAssignments = isPunjabAdmin
    ? filteredAssignments
    : allAssignments;
  const displayLogs = isPunjabAdmin ? filteredLogs : logs.results || [];

  if (!devices?.results && !allAssignments?.length && !logs?.results) {
    return (
      <Container maxWidth="xl" sx={{ mt: 10, textAlign: "center", py: 8 }}>
        <Zoom in={true}>
          <Box>
            <CircularProgress size={80} sx={{ color: "#ff4444", mb: 3 }} />
            <Typography variant="h5" sx={{ color: "#666", mb: 2 }}>
              Loading Alarm Management System
            </Typography>
            <LinearProgress
              sx={{
                maxWidth: 400,
                mx: "auto",
                borderRadius: 10,
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #ff8c00, #ff4444)",
                },
              }}
            />
            <Typography variant="body2" sx={{ color: "#999", mt: 2 }}>
              Fetching alarm devices and assignments...
            </Typography>
          </Box>
        </Zoom>
      </Container>
    );
  }

  return (
    <Fade in={true}>
      <Container maxWidth="xl" sx={{ mt: 10, mb: 4 }}>
        {/* Header with 3D effect */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            ...card3D,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                Alarm Management System
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage alarm devices, assignments, and monitor active alarms
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.7, display: "block", mt: 1 }}
              >
                Last updated: {new Date().toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              {isPunjabAdmin && (
                <Chip
                  icon={<FilterIcon />}
                  label={`Viewing ${availableCameras.length} cameras`}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    backdropFilter: "blur(10px)",
                  }}
                />
              )}
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  borderRadius: "30px",
                  px: 3,
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Active Alarms Alert */}
        {activeAlarms && activeAlarms.length > 0 && (
          <Zoom in={true}>
            <Alert
              severity="error"
              sx={{
                mb: 3,
                bgcolor: "#ff4444",
                color: "white",
                borderRadius: "15px",
                "& .MuiAlert-icon": { color: "white" },
                boxShadow: "0 4px 20px rgba(244,67,54,0.3)",
              }}
              icon={<PowerIcon />}
            >
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {activeAlarms.length} Active Alarm(s):
              </Typography>
              {activeAlarms.map((alarm, idx) => (
                <Typography key={idx} variant="body2">
                  • {alarm.alarm_device_details?.name} (
                  {alarm.alarm_device_details?.device_id}) -
                  {alarm.auto_off_at &&
                    ` Auto off: ${new Date(alarm.auto_off_at).toLocaleTimeString()}`}
                </Typography>
              ))}
            </Alert>
          </Zoom>
        )}

        {/* Tabs with 3D effect */}
        <Paper
          sx={{
            p: 2,
            mb: 4,
            ...card3D,
            background: "white",
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontSize: "1rem",
                fontWeight: 600,
                transition: "all 0.3s ease",
                borderRadius: "30px",
                minHeight: "60px",
              },
              "& .Mui-selected": {
                backgroundColor: "#ff4444",
                color: "white !important",
                transform: "scale(1.05)",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab
              label={`Alarm Devices (${displayDevices.length || 0})`}
              icon={<PowerIcon />}
              iconPosition="start"
            />
            <Tab
              label={`Assignments (${displayAssignments.length || 0})`}
              icon={<LinkIcon />}
              iconPosition="start"
            />
            <Tab
              label={`Trigger Logs (${displayLogs.length || 0})`}
              icon={<HistoryIcon />}
              iconPosition="start"
            />
            <Tab
              label={`Active Alarms (${activeAlarms?.length || 0})`}
              icon={<NotificationsActiveIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Tab 1: Alarm Devices */}
        {tab === 0 && (
          <Fade in={true}>
            <Box>
              {/* Add Device button - Only visible to superuser */}
              {isSuperUser && (
                <Box
                  sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                      bgcolor: "#ff4444",
                      color: "white",
                      borderRadius: "30px",
                      px: 4,
                      py: 1.5,
                      "&:hover": {
                        bgcolor: "#ff6666",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Add Alarm Device
                  </Button>
                </Box>
              )}

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                  ...card3D,
                }}
              >
                <Table>
                  <TableHead
                    sx={{
                      bgcolor: "#f5f5f5",
                      borderBottom: "2px solid #ff4444",
                    }}
                  >
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Device ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Connection</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Cameras</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Last Command
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Added For</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayDevices.length > 0 ? (
                      displayDevices.map((device, index) => (
                        <Fade
                          in={true}
                          key={device.id}
                          style={{ transitionDelay: `${index * 30}ms` }}
                        >
                          <TableRow
                            sx={{
                              "&:hover": {
                                bgcolor: "#f8f9fa",
                                transform: "scale(1.01)",
                                transition: "all 0.2s ease",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={device.device_id}
                                size="small"
                                sx={{
                                  bgcolor: "#333",
                                  color: "white",
                                  fontFamily: "monospace",
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {device.name}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={device.status}
                                size="small"
                                color={getStatusColor(device.status)}
                                sx={{ fontWeight: "bold" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={
                                  device.last_connected_at ? (
                                    <CheckCircleIcon />
                                  ) : (
                                    <ErrorIcon />
                                  )
                                }
                                label={
                                  device.last_connected_at
                                    ? "Connected"
                                    : "Unknown"
                                }
                                size="small"
                                color={
                                  device.last_connected_at ? "success" : "error"
                                }
                                variant="outlined"
                                sx={{
                                  color: device.last_connected_at
                                    ? "#4caf50"
                                    : "#f44336",
                                  borderColor: device.last_connected_at
                                    ? "#4caf50"
                                    : "#f44336",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={device.camera_count || 0}
                                size="small"
                                sx={{ bgcolor: "#f0f0f0", fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              {device.last_command_sent ? (
                                <Box>
                                  <Chip
                                    label={device.last_command_sent}
                                    size="small"
                                    color={
                                      device.last_command_sent === "ON"
                                        ? "success"
                                        : "error"
                                    }
                                    sx={{ mr: 1 }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#888", display: "block" }}
                                  >
                                    {new Date(
                                      device.last_command_time,
                                    ).toLocaleString()}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999" }}
                                >
                                  No commands
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  device.created_by === PUNJABADMIN_ID
                                    ? "punjabadmin"
                                    : "lumsadmin"
                                }
                                size="small"
                                color={
                                  device.created_by === PUNJABADMIN_ID
                                    ? "warning"
                                    : "info"
                                }
                                sx={{ fontWeight: "bold" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title="Test Connection">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleTestConnection(device.id)
                                    }
                                    sx={{ color: "#4caf50" }}
                                  >
                                    <HistoryIcon />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Turn ON">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleSendCommand(device, "ON")
                                    }
                                    sx={{ color: "#ff4444" }}
                                  >
                                    <PowerIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Turn OFF">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleSendCommand(device, "OFF")
                                    }
                                    sx={{ color: "#888" }}
                                  >
                                    <PowerIcon />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Assign Cameras">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenAssign(device)}
                                    sx={{ color: "#2196f3" }}
                                  >
                                    <LinkIcon />
                                  </IconButton>
                                </Tooltip>

                                {isSuperUser && (
                                  <>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenDialog(device)}
                                        sx={{ color: "#ff9800" }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDeleteDevice(device.id)
                                        }
                                        sx={{ color: "#f44336" }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </Fade>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                          <Zoom in={true}>
                            <Box>
                              <PowerIcon
                                sx={{ fontSize: 60, color: "#ccc", mb: 2 }}
                              />
                              <Typography
                                variant="h6"
                                sx={{ color: "#666", mb: 1 }}
                              >
                                No alarm devices found
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#999" }}
                              >
                                {isSuperUser
                                  ? 'Click "Add Alarm Device" to create one.'
                                  : "No devices available for your account."}
                              </Typography>
                            </Box>
                          </Zoom>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Fade>
        )}

        {/* Tab 2: Assignments */}
        {tab === 1 && (
          <Fade in={true}>
            <Box>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                  ...card3D,
                  mb: 2,
                }}
              >
                <Table>
                  <TableHead
                    sx={{
                      bgcolor: "#f5f5f5",
                      borderBottom: "2px solid #ff4444",
                    }}
                  >
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Camera</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Alarm Device
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Auto Trigger
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Assigned At
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Assigned By
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayAssignments.length > 0 ? (
                      displayAssignments
                        .slice(
                          assignmentsPage * assignmentsRowsPerPage,
                          assignmentsPage * assignmentsRowsPerPage +
                            assignmentsRowsPerPage,
                        )
                        .map((assignment, index) => (
                          <Fade
                            in={true}
                            key={assignment.id}
                            style={{ transitionDelay: `${index * 30}ms` }}
                          >
                            <TableRow
                              sx={{
                                "&:hover": {
                                  bgcolor: "#f8f9fa",
                                  transform: "scale(1.01)",
                                  transition: "all 0.2s ease",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                },
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={`#${assignment.id}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "#f0f0f0",
                                    fontWeight: 600,
                                    fontFamily: "monospace",
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {assignment.camera_details?.name ||
                                  `Camera ${assignment.camera}`}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    assignment.alarm_device_details
                                      ?.device_id ||
                                    `ID: ${assignment.alarm_device}`
                                  }
                                  size="small"
                                  sx={{ bgcolor: "#333", color: "white" }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={assignment.auto_trigger ? "Yes" : "No"}
                                  size="small"
                                  color={
                                    assignment.auto_trigger ? "success" : "info"
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {assignment.trigger_duration}s
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    assignment.is_active ? "Active" : "Inactive"
                                  }
                                  size="small"
                                  color={
                                    assignment.is_active ? "success" : "info"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDateTime(assignment.assigned_at)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    assignment.assigned_by === PUNJABADMIN_ID
                                      ? "punjabadmin"
                                      : "lumsadmin"
                                  }
                                  size="small"
                                  color={
                                    assignment.assigned_by === PUNJABADMIN_ID
                                      ? "warning"
                                      : "info"
                                  }
                                  sx={{ fontWeight: "bold" }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleOpenEditAssignment(assignment)
                                      }
                                      sx={{ color: "#ff9800" }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>

                                  {isSuperUser && (
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDeleteAssignment(assignment.id)
                                        }
                                        sx={{ color: "#f44336" }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </Fade>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                          <Zoom in={true}>
                            <Box>
                              <LinkIcon
                                sx={{ fontSize: 60, color: "#ccc", mb: 2 }}
                              />
                              <Typography
                                variant="h6"
                                sx={{ color: "#666", mb: 1 }}
                              >
                                No assignments found
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#999" }}
                              >
                                {isPunjabAdmin
                                  ? "No assignments found for your account."
                                  : "Click the Link icon on an alarm device to assign cameras."}
                              </Typography>
                            </Box>
                          </Zoom>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {displayAssignments.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={displayAssignments.length}
                  rowsPerPage={assignmentsRowsPerPage}
                  page={assignmentsPage}
                  onPageChange={handleAssignmentsChangePage}
                  onRowsPerPageChange={handleAssignmentsChangeRowsPerPage}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "15px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    ".MuiTablePagination-select": {
                      borderRadius: "15px",
                    },
                  }}
                />
              )}
            </Box>
          </Fade>
        )}

        {/* Tab 3: Trigger Logs */}
        {tab === 2 && (
          <Fade in={true}>
            <Box>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                  ...card3D,
                  mb: 2,
                }}
              >
                <Table>
                  <TableHead
                    sx={{
                      bgcolor: "#f5f5f5",
                      borderBottom: "2px solid #ff4444",
                    }}
                  >
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Alarm Device
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Trigger Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Command</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Camera</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Triggered By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayLogs.length > 0 ? (
                      displayLogs
                        .slice(
                          logsPage * logsRowsPerPage,
                          logsPage * logsRowsPerPage + logsRowsPerPage,
                        )
                        .map((log, index) => (
                          <Fade
                            in={true}
                            key={log.id}
                            style={{ transitionDelay: `${index * 30}ms` }}
                          >
                            <TableRow
                              sx={{
                                "&:hover": {
                                  bgcolor: "#f8f9fa",
                                  transform: "scale(1.01)",
                                  transition: "all 0.2s ease",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDateTime(log.created_at)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    log.alarm_device_details?.device_id ||
                                    `ID: ${log.alarm_device}`
                                  }
                                  size="small"
                                  sx={{ bgcolor: "#333", color: "white" }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.trigger_type?.replace(/_/g, " ")}
                                  size="small"
                                  color={
                                    log.trigger_type?.includes("FIRE")
                                      ? "error"
                                      : log.trigger_type?.includes("SMOKE")
                                        ? "warning"
                                        : "info"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.command_sent}
                                  size="small"
                                  color={
                                    log.command_sent === "ON"
                                      ? "success"
                                      : "error"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {log.camera_name ||
                                    (log.camera
                                      ? `Camera ${log.camera}`
                                      : "N/A")}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {log.duration ? `${log.duration}s` : "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.triggered_by_name || "N/A"}
                                  size="small"
                                  color={
                                    log.triggered_by_name === "punjabadmin"
                                      ? "warning"
                                      : "info"
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          </Fade>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <Zoom in={true}>
                            <Box>
                              <HistoryIcon
                                sx={{ fontSize: 60, color: "#ccc", mb: 2 }}
                              />
                              <Typography
                                variant="h6"
                                sx={{ color: "#666", mb: 1 }}
                              >
                                No trigger logs found
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#999" }}
                              >
                                {isPunjabAdmin
                                  ? "No trigger logs found for your account."
                                  : "Trigger an alarm to see logs here."}
                              </Typography>
                            </Box>
                          </Zoom>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {displayLogs.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={displayLogs.length}
                  rowsPerPage={logsRowsPerPage}
                  page={logsPage}
                  onPageChange={handleLogsChangePage}
                  onRowsPerPageChange={handleLogsChangeRowsPerPage}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "15px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    ".MuiTablePagination-select": {
                      borderRadius: "15px",
                    },
                  }}
                />
              )}
            </Box>
          </Fade>
        )}

        {/* Tab 4: Active Alarms */}
        {tab === 3 && (
          <Fade in={true}>
            <Grid container spacing={3}>
              {activeAlarms && activeAlarms.length > 0 ? (
                activeAlarms.map((alarm, index) => (
                  <Grid item xs={12} md={6} lg={4} key={alarm.id}>
                    <Zoom
                      in={true}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <Paper
                        sx={{
                          p: 3,
                          background:
                            "linear-gradient(145deg, #1e1e1e, #2a2a2a)",
                          borderRadius: "20px",
                          color: "white",
                          boxShadow: "0 10px 30px rgba(255,68,68,0.2)",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                          border: "1px solid #ff4444",
                          "&:hover": {
                            transform: "translateY(-5px) scale(1.02)",
                            boxShadow: "0 20px 40px rgba(255,68,68,0.3)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar
                              sx={{
                                bgcolor: "#ff4444",
                                width: 48,
                                height: 48,
                              }}
                            >
                              <WarningIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {alarm.alarm_device_details?.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#888" }}
                              >
                                {alarm.alarm_device_details?.device_id}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            icon={<NotificationsActiveIcon />}
                            label="ACTIVE"
                            size="small"
                            sx={{
                              bgcolor: "#ff4444",
                              color: "white",
                              fontWeight: "bold",
                              animation: "pulse 1s infinite",
                              "@keyframes pulse": {
                                "0%": { opacity: 1 },
                                "50%": { opacity: 0.7 },
                                "100%": { opacity: 1 },
                              },
                            }}
                          />
                        </Box>

                        <Divider sx={{ bgcolor: "#333", my: 2 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#888" }}
                            >
                              Triggered At
                            </Typography>
                            <Typography variant="body2">
                              {formatDateTime(alarm.triggered_at)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#888" }}
                            >
                              Auto Off At
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "#ff4444" }}
                            >
                              {formatDateTime(alarm.auto_off_at)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#888" }}
                            >
                              Time Remaining
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "monospace", color: "#ff9800" }}
                            >
                              {alarm.auto_off_at
                                ? Math.max(
                                    0,
                                    Math.floor(
                                      (new Date(alarm.auto_off_at) -
                                        new Date()) /
                                        1000,
                                    ),
                                  ) + "s"
                                : "N/A"}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Divider sx={{ bgcolor: "#333", my: 2 }} />

                        <Typography variant="caption" sx={{ color: "#888" }}>
                          Triggering Cameras:
                        </Typography>
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {alarm.triggering_cameras_details?.map((camera) => (
                            <Chip
                              key={camera.id}
                              label={camera.name}
                              size="small"
                              sx={{ bgcolor: "#333", color: "white" }}
                            />
                          ))}
                          {(!alarm.triggering_cameras_details ||
                            alarm.triggering_cameras_details.length === 0) && (
                            <Typography variant="body2" sx={{ color: "#888" }}>
                              No camera details
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    </Zoom>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: "center",
                      bgcolor: "#1e1e1e",
                      borderRadius: "20px",
                    }}
                  >
                    <NotificationsActiveIcon
                      sx={{ fontSize: 60, color: "#888", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ color: "#888" }}>
                      No active alarms at the moment
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Fade>
        )}

        {/* All Dialogs remain the same with updated styling */}
        {/* Add/Edit Device Dialog */}
        {isSuperUser && (
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "20px",
                overflow: "hidden",
                ...card3D,
              },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "#2a2a2a",
                color: "white",
                borderBottom: "1px solid #333",
              }}
            >
              {editingDevice ? "Edit Alarm Device" : "Add New Alarm Device"}
            </DialogTitle>
            <DialogContent sx={{ bgcolor: "#1e1e1e", py: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Device ID"
                    value={formData.device_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        device_id: e.target.value.toLowerCase(),
                      })
                    }
                    sx={{
                      input: { color: "white" },
                      label: { color: "#888" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#333" },
                        "&:hover fieldset": { borderColor: "#ff4444" },
                        "&.Mui-focused fieldset": { borderColor: "#ff4444" },
                      },
                    }}
                    placeholder="esp001"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    sx={{
                      input: { color: "white" },
                      label: { color: "#888" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#333" },
                        "&:hover fieldset": { borderColor: "#ff4444" },
                        "&.Mui-focused fieldset": { borderColor: "#ff4444" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={2}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    sx={{
                      textarea: { color: "white" },
                      label: { color: "#888" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#333" },
                        "&:hover fieldset": { borderColor: "#ff4444" },
                        "&.Mui-focused fieldset": { borderColor: "#ff4444" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="MQTT Host"
                    value={formData.mqtt_host}
                    onChange={(e) =>
                      setFormData({ ...formData, mqtt_host: e.target.value })
                    }
                    sx={{
                      input: { color: "white" },
                      label: { color: "#888" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#333" },
                        "&:hover fieldset": { borderColor: "#ff4444" },
                        "&.Mui-focused fieldset": { borderColor: "#ff4444" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="MQTT Port"
                    type="number"
                    value={formData.mqtt_port}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        mqtt_port: parseInt(e.target.value),
                      })
                    }
                    sx={{
                      input: { color: "white" },
                      label: { color: "#888" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#333" },
                        "&:hover fieldset": { borderColor: "#ff4444" },
                        "&.Mui-focused fieldset": { borderColor: "#ff4444" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="MQTT Username"
                    value={formData.mqtt_username}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        mqtt_username: e.target.value,
                      })
                    }
                    sx={{
                      input: { color: "white" },
                      label: { color: "#888" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#333" },
                        "&:hover fieldset": { borderColor: "#ff4444" },
                        "&.Mui-focused fieldset": { borderColor: "#ff4444" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="MQTT Password"
                    type="password"
                    value={formData.mqtt_password}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        mqtt_password: e.target.value,
                      })
                    }
                    sx={{
                      input: { color: "white" },
                      label: { color: "#888" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#333" },
                        "&:hover fieldset": { borderColor: "#ff4444" },
                        "&.Mui-focused fieldset": { borderColor: "#ff4444" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: "#888" }}>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                      sx={{
                        color: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#333",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ff4444",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ff4444",
                        },
                      }}
                    >
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="INACTIVE">Inactive</MenuItem>
                      <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: "#888" }}>Created For</InputLabel>
                    <Select
                      value={formData.created_by}
                      onChange={(e) => {
                        console.log("Selected user ID:", e.target.value);
                        setFormData({
                          ...formData,
                          created_by: e.target.value,
                        });
                      }}
                      sx={{
                        color: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#333",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ff4444",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ff4444",
                        },
                      }}
                    >
                      {userList.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.username}{" "}
                          {u.id === PUNJABADMIN_ID
                            ? "(punjabadmin)"
                            : u.id === LUMSADMIN_ID
                              ? "(lumsadmin)"
                              : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.mqtt_use_ssl}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mqtt_use_ssl: e.target.checked,
                          })
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#ff4444",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#ff4444",
                            },
                        }}
                      />
                    }
                    label="Use SSL"
                    sx={{ color: "white" }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{ bgcolor: "#1e1e1e", borderTop: "1px solid #333", p: 2 }}
            >
              <Button
                onClick={handleCloseDialog}
                startIcon={<CancelIcon />}
                sx={{ color: "#888" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveDevice}
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{
                  bgcolor: "#ff4444",
                  color: "white",
                  borderRadius: "30px",
                  px: 4,
                  "&:hover": {
                    bgcolor: "#ff6666",
                  },
                }}
              >
                {editingDevice ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Assign Cameras Dialog */}
        <Dialog
          open={openAssignDialog}
          onClose={handleCloseAssign}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
              ...card3D,
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#2a2a2a",
              color: "white",
              borderBottom: "1px solid #333",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <LinkIcon sx={{ color: "#2196f3" }} />
              <Typography variant="h6">
                Assign Camera to {selectedDevice?.name}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: "#1e1e1e", py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: "#888" }}>Select Camera</InputLabel>
                  <Select
                    value={assignmentForm.camera}
                    onChange={(e) =>
                      setAssignmentForm({
                        ...assignmentForm,
                        camera: e.target.value,
                      })
                    }
                    sx={{
                      color: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#333",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2196f3",
                      },
                    }}
                  >
                    {availableCameras.map((camera) => (
                      <MenuItem key={camera.id} value={camera.id}>
                        {camera.description || `Camera ${camera.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {isPunjabAdmin && (
                    <Typography variant="caption" sx={{ color: "#888", mt: 1 }}>
                      You have access to {availableCameras.length} cameras
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Trigger Duration (seconds)"
                  type="number"
                  value={assignmentForm.trigger_duration}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      trigger_duration: parseInt(e.target.value),
                    })
                  }
                  InputProps={{ inputProps: { min: 10, max: 600 } }}
                  sx={{
                    input: { color: "white" },
                    label: { color: "#888" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#333" },
                      "&:hover fieldset": { borderColor: "#2196f3" },
                    },
                  }}
                  helperText="How long the alarm should stay ON (in seconds)"
                  FormHelperTextProps={{ sx: { color: "#888" } }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={assignmentForm.auto_trigger}
                      onChange={(e) =>
                        setAssignmentForm({
                          ...assignmentForm,
                          auto_trigger: e.target.checked,
                        })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4caf50",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                          },
                      }}
                    />
                  }
                  label="Auto-trigger when fire/smoke detected"
                  sx={{ color: "white" }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={assignmentForm.is_active}
                      onChange={(e) =>
                        setAssignmentForm({
                          ...assignmentForm,
                          is_active: e.target.checked,
                        })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4caf50",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                          },
                      }}
                    />
                  }
                  label="Assignment Active"
                  sx={{ color: "white" }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{ borderTop: "1px solid #333", p: 2, bgcolor: "#1e1e1e" }}
          >
            <Button
              onClick={handleCloseAssign}
              startIcon={<CancelIcon />}
              sx={{ color: "#888" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignment}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!assignmentForm.camera}
              sx={{
                bgcolor: "#2196f3",
                color: "white",
                borderRadius: "30px",
                px: 4,
                "&:hover": {
                  bgcolor: "#1976d2",
                },
                "&.Mui-disabled": { bgcolor: "#333", color: "#666" },
              }}
            >
              Assign Camera
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Assignment Dialog */}
        <Dialog
          open={openEditAssignmentDialog}
          onClose={handleCloseEditAssignment}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
              ...card3D,
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#2a2a2a",
              color: "white",
              borderBottom: "1px solid #333",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <EditIcon sx={{ color: "#ff9800" }} />
              <Typography variant="h6">Edit Assignment</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: "#1e1e1e", py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#888", mb: 1 }}>
                  Camera: {editingAssignment?.camera_details?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "#888", mb: 2 }}>
                  Alarm Device: {editingAssignment?.alarm_device_details?.name}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Trigger Duration (seconds)"
                  type="number"
                  value={assignmentForm.trigger_duration}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      trigger_duration: parseInt(e.target.value),
                    })
                  }
                  InputProps={{ inputProps: { min: 10, max: 600 } }}
                  sx={{
                    input: { color: "white" },
                    label: { color: "#888" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#333" },
                      "&:hover fieldset": { borderColor: "#ff9800" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={assignmentForm.auto_trigger}
                      onChange={(e) =>
                        setAssignmentForm({
                          ...assignmentForm,
                          auto_trigger: e.target.checked,
                        })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4caf50",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                          },
                      }}
                    />
                  }
                  label="Auto-trigger when fire/smoke detected"
                  sx={{ color: "white" }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={assignmentForm.is_active}
                      onChange={(e) =>
                        setAssignmentForm({
                          ...assignmentForm,
                          is_active: e.target.checked,
                        })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4caf50",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                          },
                      }}
                    />
                  }
                  label="Assignment Active"
                  sx={{ color: "white" }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{ borderTop: "1px solid #333", p: 2, bgcolor: "#1e1e1e" }}
          >
            <Button
              onClick={handleCloseEditAssignment}
              startIcon={<CancelIcon />}
              sx={{ color: "#888" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateAssignment}
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{
                bgcolor: "#ff9800",
                color: "white",
                borderRadius: "30px",
                px: 4,
                "&:hover": {
                  bgcolor: "#f57c00",
                },
              }}
            >
              Update Assignment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Live Test Dialog */}
        {isSuperUser && (
          <Dialog
            open={openTestDialog}
            onClose={handleCloseTest}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "20px",
                overflow: "hidden",
                ...card3D,
              },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "#2a2a2a",
                color: "white",
                borderBottom: "1px solid #333",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon sx={{ color: "#ff4444" }} />
                <Typography variant="h6">
                  Live Alarm Test - {testingDevice?.name}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: "#1e1e1e", py: 3 }}>
              {testingDevice && (
                <Alarm
                  deviceId={testingDevice.device_id}
                  deviceName={testingDevice.name}
                  showControls={true}
                  triggerDuration={180}
                  mqttConfig={{
                    host: testingDevice.mqtt_host,
                    port: testingDevice.mqtt_port,
                    username: testingDevice.mqtt_username,
                    password: testingDevice.mqtt_password,
                    useSSL: testingDevice.mqtt_use_ssl,
                  }}
                />
              )}
            </DialogContent>
            <DialogActions
              sx={{ borderTop: "1px solid #333", p: 2, bgcolor: "#1e1e1e" }}
            >
              <Button
                onClick={handleCloseTest}
                variant="contained"
                sx={{
                  bgcolor: "#ff4444",
                  color: "white",
                  borderRadius: "30px",
                  px: 4,
                  "&:hover": {
                    bgcolor: "#ff6666",
                  },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Container>
    </Fade>
  );
};

export default AlarmManagement;
